import {
  nextActivitySort,
  sortParticipants,
  sortParticipantTasks,
} from '../../domain/campaignActivity/sorting';
import type {
  ActivitySort,
  ParticipantSortKey,
  TaskSortKey,
} from '../../domain/campaignActivity/sorting';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  buildParticipantRows,
  canReviewCampaign,
} from '../../domain/campaignActivity/participants';
import type { ParticipantRole } from '../../domain/campaignActivity/participants';
import { listActivityForTasks } from '../../services/taskActivity';
import { adminAssignReview, listTasksForCampaign, releaseReview } from '../../services/tasks';
import type { Task, TaskActivity, User } from '../../types';
import { updateUserRoles } from '../../services/users';
import { downloadBlob } from '../../services/taskExport';
import { isTaskCommandError } from '../../services/taskCommands';
import { categorizeError } from '../../utils/errorCategory';
import { logGlobalError } from '../../utils/globalErrorLogger';

export function useAdminCampaignActivityViewModel(
  campaignId: string,
  campaignName: string,
  users: User[],
  writerIds?: string[],
) {
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [source, setSource] = useState<{ tasks: Task[]; activity: TaskActivity[] }>({
    tasks: [],
    activity: [],
  });
  const [mode, setMode] = useState<ParticipantRole | 'eventLog'>('writers');
  const [current, setCurrent] = useState(true);
  const [sorts, setSorts] = useState<Record<ParticipantRole, ActivitySort<ParticipantSortKey>>>({
    writers: { key: 'name', direction: 'ascending' },
    reviewers: { key: 'name', direction: 'ascending' },
  });
  const [taskSorts, setTaskSorts] = useState<Record<ParticipantRole, ActivitySort<TaskSortKey>>>({
    writers: { key: 'id', direction: 'ascending' },
    reviewers: { key: 'id', direction: 'ascending' },
  });
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60000);
    return () => window.clearInterval(timer);
  }, []);
  const role = mode === 'reviewers' ? 'reviewers' : 'writers';
  const sort = sorts[role];
  const taskSort = taskSorts[role];
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [reassignId, setReassignId] = useState<string | null>(null);
  const [reassignTask, setReassignTask] = useState<Task | null>(null);
  const [targetId, setTargetId] = useState('');
  const [pending, setPending] = useState(false);
  const [trustedShineId, setTrustedShineId] = useState<string | null>(null);
  const [trustedOverrides, setTrustedOverrides] = useState<Record<string, string[]>>({});
  const [notice, setNotice] = useState<string | null>(null);
  const generation = useRef(0);
  const actionLock = useRef(false);
  const refresh = useCallback(async () => {
    const request = ++generation.current;
    setBusy(true);
    setError(null);
    try {
      const tasks = await listTasksForCampaign(campaignId);
      const activity = await listActivityForTasks(tasks.map((t) => t.id));
      if (request === generation.current) {
        setSource({ tasks, activity });
        setNow(Date.now());
      }
    } catch (cause) {
      if (request === generation.current)
        setError(cause instanceof Error ? cause.message : 'Unable to load activity.');
      logGlobalError(cause, { context: 'AdminCampaignActivity.refresh', metadata: { campaignId } });
    } finally {
      if (request === generation.current) setBusy(false);
    }
  }, [campaignId]);
  useEffect(() => {
    setSource({ tasks: [], activity: [] });
    void refresh();
    return () => {
      generation.current += 1;
    };
  }, [refresh]);
  const rows = useMemo(
    () =>
      buildParticipantRows({
        ...source,
        users,
        campaignId,
        role: mode === 'reviewers' ? 'reviewers' : 'writers',
        current,
        writerIds,
        now,
      }),
    [source, users, campaignId, mode, current, writerIds, now],
  );
  const trustedIds = (id: string) => trustedOverrides[id] ?? users.find((user) => user.uid === id)?.trustedCampaignIds ?? [];
  const isTrusted = (id: string) => trustedIds(id).includes(campaignId);
  const query = searchQuery.trim().toLowerCase();
  const visibleRows = sortParticipants(
    rows.filter((r) => `${r.name} ${r.email} ${r.expertId} ${r.id}`.toLowerCase().includes(query))
      .map((row) => ({ ...row, trusted: isTrusted(row.id) })),
    sort,
  ).map((row) => ({
    ...row,
    tasks: sortParticipantTasks(
      current && role === 'reviewers'
        ? row.tasks.filter(({ task }) => task.status === 'in_review' && task.reviewerId === row.id)
        : row.tasks,
      taskSort,
    ),
  }));
  const events = useMemo(
    () =>
      [...source.activity]
        .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
        .filter((e) =>
          `${e.actorName} ${e.actorId} ${e.taskId} ${e.taskTitle} ${e.kind}`
            .toLowerCase()
            .includes(query),
        ),
    [source, query],
  );
  const pageCount = Math.max(1, Math.ceil(events.length / 25));
  const activePage = Math.min(page, pageCount - 1);
  const reviewers = users.filter((u) => canReviewCampaign(u, campaignId));
  const assignedTasks = (id: string) =>
    source.tasks.filter((t) => t.status === 'in_review' && t.reviewerId === id);
  const bulkUpdate = async (id: string, target?: string, selectedTask?: Task) => {
    if (actionLock.current) return;
    actionLock.current = true;
    setPending(true);
    setNotice(null);
    const tasks = selectedTask ? [selectedTask] : assignedTasks(id);
    let failed = 0;
    try {
      for (let i = 0; i < tasks.length; i += 4) {
        const results = await Promise.allSettled(
          tasks
            .slice(i, i + 4)
            .map((t) =>
              target
                ? adminAssignReview(t.id, target, t.reviewRound, id)
                : releaseReview(t.id, t.reviewRound, id),
            ),
        );
        for (const result of results) {
          if (result.status === 'rejected') {
            failed += 1;
            const category = categorizeError(result.reason);
            if (
              !isTaskCommandError(result.reason) &&
              !['auth', 'network'].includes(category.type)
            ) {
              logGlobalError(result.reason, {
                context: 'AdminCampaignActivity.bulkUpdate',
                metadata: { campaignId, reviewerId: id },
              });
            }
          }
        }
      }
      setNotice(
        `${tasks.length - failed} ${tasks.length - failed === 1 ? 'task' : 'tasks'} ${target ? 'reassigned' : 'released'}.${failed ? ` ${failed} could not be changed; refresh and try again.` : ''}`,
      );
      setReassignId(null);
      setTargetId('');
      await refresh();
    } finally {
      actionLock.current = false;
      setPending(false);
    }
  };
  const exportCsv = () => {
    const values: unknown[][] =
      mode === 'eventLog'
        ? [
            ['Date', 'Event', 'Actor', 'Task ID', 'From', 'To'],
            ...events.map((event) => [
              event.createdAt.toISOString(),
              event.kind,
              event.actorName,
              event.taskId,
              event.fromStatus,
              event.toStatus,
            ]),
          ]
        : [
            [
              'Name',
              'Email',
              'Expert ID',
              'Signed off tasks',
              'In-review tasks',
              mode === 'writers' ? 'Average writing time (hours)' : 'Average review time (hours)',
              'Revision ratio',
              'Task IDs',
            ],
            ...visibleRows.map((row) => [
              row.name,
              row.email,
              row.expertId,
              row.signedOff,
              row.inReview,
              (mode === 'writers' ? row.averageWritingMs : row.averageReviewMs) === null
                ? ''
                : (mode === 'writers' ? row.averageWritingMs! : row.averageReviewMs!) / 3600000,
              mode === 'writers' ? row.revisionRatio : '',
              row.tasks.map((item) => item.task.id).join('; '),
            ]),
          ];
    const csv = values
      .map((row) => row.map((value) => `"${String(value ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    downloadBlob(
      csv,
      `campaign_activity_${campaignId}_${mode}_${current ? 'current' : 'all_time'}.csv`,
      'text/csv;charset=utf-8',
    );
  };
  const toggleTrusted = async (id: string) => {
    if (actionLock.current) return;
    actionLock.current = true;
    setPending(true);
    setNotice(null);
    const ids = trustedIds(id);
    const next = isTrusted(id) ? ids.filter((value) => value !== campaignId) : [...ids, campaignId];
    try {
      await updateUserRoles(id, { trustedCampaignIds: next });
      setTrustedOverrides((previous) => ({ ...previous, [id]: next }));
      setTrustedShineId(next.includes(campaignId) ? id : null);
    } catch (cause) {
      setNotice(cause instanceof Error ? cause.message : 'Unable to update trusted status.');
      logGlobalError(cause, { context: 'AdminCampaignActivity.toggleTrusted', metadata: { campaignId, uid: id } });
    } finally {
      actionLock.current = false;
      setPending(false);
    }
  };
  return {
    trustedShineId,
    clearTrustedShine: () => setTrustedShineId(null),
    isTrusted,
    toggleTrusted,
    canSetTrusted: (id: string) => users.some((user) => user.uid === id),
    busy,
    exportCsv,
    error,
    mode,
    current,
    searchQuery,
    visibleRows,
    sort,
    taskSort,
    setSort: (key: ParticipantSortKey) =>
      setSorts((previous) => ({
        ...previous,
        [role]: nextActivitySort(previous[role], key, key !== 'name'),
      })),
    setTaskSort: (key: TaskSortKey) =>
      setTaskSorts((previous) => ({
        ...previous,
        [role]: nextActivitySort(
          previous[role],
          key,
          ['reviewMs', 'progressMs', 'sinceLastActivityMs'].includes(key),
        ),
      })),
    reviewers,
    expanded,
    visibleEvents: events.slice(activePage * 25, activePage * 25 + 25),
    eventCount: events.length,
    page: activePage,
    pageCount,
    pending,
    notice,
    reassignId,
    reassignTask,
    openReassign: (reviewerId: string, task?: Task) => {
      setTargetId('');
      setReassignId(reviewerId);
      setReassignTask(task ?? null);
    },
    targetId,
    campaignName,
    setMode: (value: ParticipantRole | 'eventLog') => {
      setMode(value);
      setSearchQuery('');
      setPage(0);
    },
    setCurrent,
    setSearchQuery: (value: string) => {
      setSearchQuery(value);
      setPage(0);
    },
    setPage,
    toggleExpanded: (id: string) =>
      setExpanded((old) => {
        const next = new Set(old);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      }),
    setReassignId,
    setTargetId,
    assignedTasks,
    bulkUpdate,
    refresh,
  };
}
